import { Button, Input, theme } from '@innovamat/glimmer-components';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import {
  AnchorStyled,
  Footer,
  Form,
  KnowMore,
  LinkStyled,
  Separator,
  SeparatorWrapper,
  SignInWrapper,
} from './components/login.styled';

import styled from '@emotion/styled';
import {
  ClassLinkImg,
  CleverImg,
  GoogleImg,
  MicrosoftImg,
  HeliLoading,
} from '@innovamat/glimmer-assets';
import { validators } from '@innovamat/radiance-utils';
import type { RosterStreamProvider } from '@innovamat/social-login';
import { useWebviewBridge } from '@innovamat/webview-bridge';
import { Helmet } from 'react-helmet';
import { useUser } from '../../../user-management';
import LoginRegisterLayout from '../../components/login-register-layout/login-register-layout';
import { SignInButton } from '../../components/sign-in-button';
import { useLogin } from '../../hooks/use-login';
import WebViewModal from './components/web-view-modal';

const FullScreenOverlayForWebview = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 9999;
  background-color: #fff;
`;

const FullScreenOverlayForDemoUser = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${theme.colors.white};
  z-index: 9999;
`;

const DemoUserNotification = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 128px;
  background-color: ${theme.colors.white};
  color: ${theme.colors.dark['01']};
  text-align: center;
  gap: 32px;
`;

const DemoUserLogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const HELI_LOADING_SRC =
  typeof HeliLoading === 'string'
    ? HeliLoading
    : (HeliLoading as { src: string }).src;

//TODO: en el DS no está definido este color que viene de innova-components
const LoginButton = styled(Button)`
  &:not(:disabled) {
    background-color: #333;
  }
`;

function Login(): JSX.Element {
  const { t } = useTranslation();
  const {
    handleSetCredentials,
    onLogin,
    credentials,
    notification,
    showPassword,
    handleToggleShowPassword,
    isLoading,
    handleDisplayError,
    isValid,
    showWebViewModal,
    handleHideWebViewModal,
    handleCheckEmail,
    showEmailError,
    showCleverBtn,
    showClassLinkBtn,
    handleIsUsa,
  } = useLogin();

  const { isLoggedIn } = useUser();

  const { username, password } = credentials;

  const handleLogin = (e: React.FormEvent): void => {
    e.preventDefault();
    onLogin({ type: 'users', payload: { username, password } });
  };

  const onRosterStreamLogin = (provider: RosterStreamProvider): void => {
    onLogin({ type: 'roster_stream', payload: { provider } });
  };

  const handleFocus = (): void => {
    handleDisplayError(false);
  };

  const isDemoUser = useMemo(
    () => window.sessionStorage.getItem('isDemoUser'),
    []
  );

  const onUserDemoLogin = useCallback((): void => {
    onLogin({
      type: 'users',
      payload: {
        username: process.env.NEXT_PUBLIC_DEMO_USERNAME as string,
        password: process.env.NEXT_PUBLIC_DEMO_PASSWORD as string,
      },
    });
  }, [onLogin]);

  const hasTriggeredDemoLogin = useRef(false);

  useEffect(() => {
    if (hasTriggeredDemoLogin.current) return;
    if (typeof window === 'undefined') return;

    if (isDemoUser !== 'true') return;

    hasTriggeredDemoLogin.current = true;
    onUserDemoLogin();
  }, [onUserDemoLogin, isDemoUser]);

  const { isInIOS, isInWebview, isGreenApp } = useWebviewBridge();
  const isInIOSAndInWebview = isInIOS && isInWebview;

  const { i18n } = useTranslation();
  const language = i18n.language;

  if (isLoggedIn) {
    return <Navigate to={`/`} />;
  }

  if (isDemoUser === 'true') {
    return (
      <FullScreenOverlayForDemoUser>
        <DemoUserNotification>
          <DemoUserLogoContainer>
            <img alt="Loading" src={HELI_LOADING_SRC} height={48} />
          </DemoUserLogoContainer>
          <h3>Launching your demo environment...</h3>
        </DemoUserNotification>
      </FullScreenOverlayForDemoUser>
    );
  }
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {isInWebview && isGreenApp && <FullScreenOverlayForWebview />}
      <LoginRegisterLayout
        title={t('Login.Accedeix a Innovamat')}
        notification={notification}
        hasLanguageDropdown
        handleIsUSA={handleIsUsa}
      >
        <>
          <Form onSubmit={handleLogin}>
            <Input
              data-cy="loginEmail"
              dataTestId="loginEmail"
              onFocus={handleFocus}
              labelText={t('Login.Nom d’usuari o email')}
              required
              placeholder={t('Login.Nom d’usuari o email')}
              onChange={(e) => handleSetCredentials(e.target.value, 'username')}
              value={username}
              type="email"
              descriptiveText={
                showEmailError && !validators.isEmailValid(username)
                  ? t('common.email.error')
                  : ''
              }
              status={
                showEmailError && !validators.isEmailValid(username)
                  ? 'error'
                  : undefined
              }
              onBlur={() => handleCheckEmail(username)}
            />
            <Input
              data-cy="loginPassword"
              dataTestId="loginPassword"
              onFocus={handleFocus}
              labelText={t('Login.Contrassenya')}
              required
              type={showPassword ? 'text' : 'password'}
              value={password}
              placeholder={t('Login.Contrassenya')}
              onChange={(e) => handleSetCredentials(e.target.value, 'password')}
              descriptiveText={t(
                'Login.password.descriptionLength',
                'Mínimo 6 caracteres, combinando letras y números.'
              )}
              rightIcon={showPassword ? 'ViewOffIcon' : 'ViewIcon'}
              onIconClick={() => handleToggleShowPassword()}
            />
            <LoginButton
              fill
              data-cy="loginButton"
              data-testid="loginButton"
              type="submit"
              disabled={!isValid() || isLoading}
              onClick={handleLogin}
              loading={isLoading}
            >
              <strong>{t('Login.Accedir')}</strong>
            </LoginButton>
          </Form>

          <Footer>
            <LinkStyled
              to={{
                pathname: `/${language}/forgot-password`,
              }}
              data-testid="forgotPassword"
            >
              {t('Login.Has oblidat la contrassenya?')}
            </LinkStyled>
            {!isInIOSAndInWebview && (
              <>
                <SeparatorWrapper>
                  <Separator />
                  <span>{t('Login.msal.or')}</span>
                  <Separator />
                </SeparatorWrapper>
                <SignInWrapper>
                  <SignInButton
                    dataCy="signInButtonGoogle"
                    dataTestid="signInButtonGoogle"
                    onClick={() => onRosterStreamLogin('google')}
                    Logo={GoogleImg}
                    text={t('Login.google.button', 'Inicia sesión con Google')}
                  />
                  <SignInButton
                    dataCy="signInButtonMicrosoft"
                    dataTestid="signInButtonMicrosoft"
                    onClick={() => onRosterStreamLogin('microsoft')}
                    Logo={MicrosoftImg}
                    text={t('Login.msal.button', 'Inicia sesión con Microsoft')}
                  />
                  {showCleverBtn && (
                    <SignInButton
                      dataCy="signInButtonClever"
                      dataTestid="signInButtonClever"
                      onClick={() => onRosterStreamLogin('clever')}
                      Logo={CleverImg}
                      text={t(
                        'Login.clever.button',
                        'Inicia sesión con Clever'
                      )}
                    />
                  )}
                  {showClassLinkBtn && (
                    <SignInButton
                      dataCy="signInButtonRosterStream"
                      dataTestid="signInButtonRosterStream"
                      Logo={ClassLinkImg}
                      onClick={() => onRosterStreamLogin('classlink')}
                      text={t(
                        'Login.classlink.button',
                        'Sign in with ClassLink'
                      )}
                    />
                  )}
                </SignInWrapper>
              </>
            )}
            <WebViewModal
              show={showWebViewModal}
              onCancel={handleHideWebViewModal}
            />

            {!isInIOSAndInWebview && (
              <KnowMore>
                <span>{t('Login.knowMoreAboutInnovamat')}</span>
                &nbsp;
                <AnchorStyled
                  href={t('common.innovamat_website')}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="knowMore"
                >
                  {t('Login.clickHere')}
                </AnchorStyled>
              </KnowMore>
            )}
          </Footer>
        </>
      </LoginRegisterLayout>
    </>
  );
}

export { Login };
